// Servicio de clima — replica WeatherRepository.kt de la app Android Aldahra.
//
// Flujo:
//   1. Toma la dirección de una fábrica + una fecha (YYYY-MM-DD).
//   2. Limpia la dirección (quita el email entre paréntesis, normaliza,
//      elimina palabras-ruido como "calle", "avenida", "s/n"...) y genera
//      varios candidatos en orden de prioridad.
//   3. Geocodifica cada candidato vía OpenMeteo Geocoding hasta obtener
//      coordenadas. Cachea los hits (memoria) y los miss (con TTL corto).
//   4. Pide el pronóstico:
//        - api.open-meteo.com/v1/forecast  para hoy y futuro / pasado <90d
//        - archive-api.open-meteo.com/v1/archive  para fechas más antiguas
//   5. Cachea el resultado con TTL variable (30m hoy / 2h futuro / ∞ pasado).
//
// La API NO requiere key. Funciona desde cualquier origen (CORS abierto).
//
// Diseño compatible con offline:
//   - Si `isOfflineForUser()` devuelve true (sin red o modo offline manual),
//     se devuelve solo lo que haya en caché de memoria; nunca se llama HTTP.
//   - El SW además cachea las respuestas HTTP de OpenMeteo (config en sw.ts),
//     así que al volver a abrir la app pre-cargada, el clima del día sale
//     del cache del navegador sin tocar red.
import { isOfflineForUser } from './networkStatus';

// ── Constantes (espejo de WeatherRepository.kt) ───────────────────────────

const TODAY_TTL_MS = 30 * 60 * 1000;
const FUTURE_TTL_MS = 2 * 60 * 60 * 1000;
const PAST_TTL_MS = Number.MAX_SAFE_INTEGER;
const NEGATIVE_TTL_MS = 10 * 60 * 1000;
const HTTP_TIMEOUT_MS = 5_000;
const ARCHIVE_THRESHOLD_DAYS = -90;

// Palabras que no aportan valor para el geocoder y conviene eliminar.
const NOISE_WORDS = new Set([
  's/n', 'sn', 'calle', 'avenida', 'av', 'num', 'nro', 'no', 'polig',
  'poligono', 'pol', 'pol.', 'industrial', 'de', 'del', 'la', 'el',
  'los', 'las', 'y', 'con', 'sin',
]);

// ── Tipos ──────────────────────────────────────────────────────────────────

export interface WeatherInfo {
  temperatureC: number;
  weatherCode: number;
}
interface GeoPoint { lat: number; lon: number }
interface CachedWeather { info: WeatherInfo; fetchedAt: number }

type DateClass = 'past' | 'today' | 'future';

// ── Caches en memoria ──────────────────────────────────────────────────────

const geocodeCache = new Map<string, GeoPoint>();
const geocodeMisses = new Map<string, number>();
const weatherCache = new Map<string, CachedWeather>();

// Mutex por (address|date): si dos componentes piden el mismo clima al mismo
// tiempo, comparten la promise en vuelo en lugar de duplicar la petición.
const inFlight = new Map<string, Promise<WeatherInfo | null>>();

// ── API pública ────────────────────────────────────────────────────────────

export async function fetchWeatherByAddress(
  rawAddress: string | undefined,
  dateIso: string,
): Promise<WeatherInfo | null> {
  if (!rawAddress || !dateIso) return null;

  const cacheKey = `${rawAddress.toLowerCase().trim()}|${dateIso}`;
  const now = Date.now();

  // Cache hit con TTL aún válido → devolver al instante.
  const cached = weatherCache.get(cacheKey);
  if (cached && (now - cached.fetchedAt) < ttlForDate(dateIso)) {
    return cached.info;
  }

  // Sin red o modo offline manual: solo lo que tengamos en cache (aunque
  // esté caducado). Mejor mostrar dato viejo que nada.
  if (isOfflineForUser()) {
    return cached?.info ?? null;
  }

  // Negative cache: si el geocode falló hace poco para esta dirección, no
  // martilleamos al servidor.
  const missAt = geocodeMisses.get(cacheKey);
  if (missAt && (now - missAt) < NEGATIVE_TTL_MS) return null;

  // Mutex: una sola petición en vuelo por clave.
  const existing = inFlight.get(cacheKey);
  if (existing) return existing;

  const promise = doFetch(rawAddress, dateIso, cacheKey);
  inFlight.set(cacheKey, promise);
  try {
    return await promise;
  } finally {
    inFlight.delete(cacheKey);
  }
}

// ── Implementación ─────────────────────────────────────────────────────────

async function doFetch(
  rawAddress: string,
  dateIso: string,
  cacheKey: string,
): Promise<WeatherInfo | null> {
  try {
    const candidates = buildCandidates(rawAddress);
    if (candidates.length === 0) return null;

    let coords: GeoPoint | null = null;
    for (const c of candidates) {
      const cached = geocodeCache.get(c);
      if (cached) { coords = cached; break; }
      const got = await geocode(c);
      if (got) {
        geocodeCache.set(c, got);
        coords = got;
        break;
      }
    }
    if (!coords) {
      geocodeMisses.set(cacheKey, Date.now());
      return null;
    }

    const info = await fetchForecast(coords.lat, coords.lon, dateIso);
    if (info) weatherCache.set(cacheKey, { info, fetchedAt: Date.now() });
    return info;
  } catch {
    return null;
  }
}

// Limpia y genera variantes de la dirección. Replica buildCandidates() de
// Android: quita el email entre paréntesis, normaliza minúsculas, sustituye
// dígitos y símbolos por espacios, filtra palabras-ruido.
function buildCandidates(raw: string): string[] {
  let cleaned = raw.trim().toLowerCase();
  // Quita "(email@x.com)" y similares.
  cleaned = cleaned.replace(/\s*\([^)]*\)\s*$/, '').trim();
  // Sustituye dígitos y símbolos por espacios (preservamos letras con tilde).
  const symbolless = cleaned.replace(/[0-9.,;:_\-/\\#·"'`*+!?¿¡]/g, ' ');
  const tokens = symbolless.split(/\s+/).filter((t) => {
    if (!t || t.length < 3) return false;
    return !NOISE_WORDS.has(t);
  });
  const out: string[] = [];
  if (tokens.length > 0) out.push(tokens.join(' '));
  if (tokens.length >= 2) out.push(tokens.slice(-2).join(' '));
  if (tokens.length >= 1) out.push(tokens[tokens.length - 1]);
  if (cleaned && !out.includes(cleaned)) out.push(cleaned);
  // Dedup manteniendo orden.
  return Array.from(new Set(out));
}

async function geocode(query: string): Promise<GeoPoint | null> {
  const url = `https://geocoding-api.open-meteo.com/v1/search?count=1&language=es&name=${encodeURIComponent(query)}`;
  const json = await fetchJson(url);
  if (!json) return null;
  const r = (json as { results?: Array<{ latitude?: number; longitude?: number }> }).results;
  const first = r?.[0];
  if (!first || typeof first.latitude !== 'number' || typeof first.longitude !== 'number') {
    return null;
  }
  return { lat: first.latitude, lon: first.longitude };
}

async function fetchForecast(
  lat: number,
  lon: number,
  dateIso: string,
): Promise<WeatherInfo | null> {
  const diff = daysDiffFromToday(dateIso) ?? 0;
  const baseUrl = diff < ARCHIVE_THRESHOLD_DAYS
    ? 'https://archive-api.open-meteo.com/v1/archive'
    : 'https://api.open-meteo.com/v1/forecast';
  const url = `${baseUrl}?latitude=${lat}&longitude=${lon}` +
    `&daily=temperature_2m_max,weather_code&timezone=auto` +
    `&start_date=${dateIso}&end_date=${dateIso}`;
  const json = await fetchJson(url);
  if (!json) return null;
  const daily = (json as {
    daily?: { temperature_2m_max?: number[]; weather_code?: number[] };
  }).daily;
  const t = daily?.temperature_2m_max?.[0];
  const c = daily?.weather_code?.[0];
  if (typeof t !== 'number' || typeof c !== 'number') return null;
  return { temperatureC: t, weatherCode: c };
}

// ── Helpers ────────────────────────────────────────────────────────────────

async function fetchJson(url: string): Promise<unknown> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), HTTP_TIMEOUT_MS);
  try {
    const r = await fetch(url, { signal: ctrl.signal });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function ttlForDate(dateIso: string): number {
  switch (classifyDate(dateIso)) {
    case 'today':  return TODAY_TTL_MS;
    case 'future': return FUTURE_TTL_MS;
    case 'past':   return PAST_TTL_MS;
  }
}

function classifyDate(dateIso: string): DateClass {
  const diff = daysDiffFromToday(dateIso);
  if (diff == null) return 'today';
  if (diff < 0) return 'past';
  if (diff === 0) return 'today';
  return 'future';
}

function daysDiffFromToday(dateIso: string): number | null {
  const m = dateIso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const target = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / (24 * 60 * 60 * 1000));
}
